import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import supabase from '../supabaseClient';

const REMEMBER_ME_KEY = 'zenith_remember_me';
const TELEGRAM_EMAIL_PATTERN = /^tg_\d+@telegram\.zenithexpense\.xyz$/;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user chose NOT to be remembered
    // If rememberMe is not set and session exists, check if it's a new browser session
    const checkRememberMe = async () => {
      const rememberMe = localStorage.getItem(REMEMBER_ME_KEY);
      const sessionMarker = sessionStorage.getItem('zenith_session_active');

      // Get current session
      const { data: { session: currentSession } } = await supabase.auth.getSession();

      // If user exists, rememberMe is explicitly 'false', and this is a new browser session
      // (sessionStorage marker is missing), sign them out
      if (currentSession && rememberMe === 'false' && !sessionMarker) {
        console.log('🔐 Session not remembered, signing out');
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      // Mark this browser session as active
      if (currentSession) {
        sessionStorage.setItem('zenith_session_active', 'true');
      }

      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    };

    checkRememberMe();

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Mark session as active when user signs in
      if (session) {
        sessionStorage.setItem('zenith_session_active', 'true');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Note: OAuth callback is handled automatically by Supabase SDK (implicit flow).
  // On web: SDK detects #access_token in URL hash on page load.
  // On Android: native MainActivity.handleDeepLink loads the callback URL
  // (with hash fragment) into the WebView, then SDK picks up the tokens.

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email, password, rememberMe = true) => {
    // Store remember me preference
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? 'true' : 'false');

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    // Mark this session as active
    sessionStorage.setItem('zenith_session_active', 'true');

    return data;
  };

  const signOut = async () => {
    // Clear remember me preference on explicit sign out
    localStorage.removeItem(REMEMBER_ME_KEY);
    sessionStorage.removeItem('zenith_session_active');

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const signInWithOAuth = async (provider, rememberMe = true) => {
    // Store remember me preference before OAuth redirect
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? 'true' : 'false');

    // Android app: use native Google Sign-In via Play Services
    // The native JS bridge (window.NativeGoogleSignIn) is injected by MainActivity.java
    if (provider === 'google' && window.NativeGoogleSignIn) {
      return new Promise((resolve, reject) => {
        const handler = (event) => {
          window.removeEventListener('nativeGoogleSignIn', handler);
          const { idToken, error: nativeError } = event.detail;
          if (nativeError) {
            reject(new Error(nativeError));
            return;
          }
          // Exchange the Google ID token for a Supabase session
          supabase.auth.signInWithIdToken({
            provider: 'google',
            token: idToken,
          }).then(({ data, error: supaError }) => {
            if (supaError) {
              reject(supaError);
            } else {
              sessionStorage.setItem('zenith_session_active', 'true');
              resolve(data);
            }
          });
        };
        window.addEventListener('nativeGoogleSignIn', handler);
        window.NativeGoogleSignIn.signIn();
      });
    }

    // Web: use default OAuth redirect flow
    const redirectUrl = `${window.location.origin}/`;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          prompt: 'select_account',
        },
      },
    });
    if (error) throw error;
    return data;
  };

  const signInWithMagicLink = async (email) => {
    localStorage.setItem(REMEMBER_ME_KEY, 'true');

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signInWithTelegramData = async (telegramData) => {
    // POST widget data to backend for HMAC verification + session creation
    const response = await fetch(`${API_URL}/api/auth/telegram-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(telegramData),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.message || 'Telegram login failed');
    }

    // Use the token_hash from backend to establish a Supabase session
    const { error } = await supabase.auth.verifyOtp({
      token_hash: result.token_hash,
      type: result.type || 'magiclink',
    });
    if (error) throw error;

    localStorage.setItem(REMEMBER_ME_KEY, 'true');
    sessionStorage.setItem('zenith_session_active', 'true');
  };

  const getAccessToken = () => {
    return session?.access_token || null;
  };

  // Detect if the user logged in via Telegram without a real email
  const isTelegramOnlyUser = useMemo(() => {
    if (!user?.email) return false;
    return TELEGRAM_EMAIL_PATTERN.test(user.email);
  }, [user]);

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithOAuth,
    signInWithMagicLink,
    signInWithTelegramData,
    signOut,
    getAccessToken,
    isTelegramOnlyUser,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
